import React, { useState } from "react";
import "./UISwitch.css";
import { chooseClassicUI, chooseModernUI } from "../lib/uiPreference";

// First-visit-only coachmark — a first-time user has no reason to already
// know what "Classic" vs "Updated UI" means; this toggle sat unexplained in
// the header otherwise. Same version-keyed localStorage seen-flag pattern
// as useReleaseNotes.js's "What's New" modal (see seenKey there).
const COACHMARK_KEY = "ui-switch.coachmark.v1.seen";
const hasSeenCoachmark = () => {
  if (typeof window === "undefined") return true;
  try { return window.localStorage.getItem(COACHMARK_KEY) === "true"; } catch (_) { return false; }
};
const markCoachmarkSeen = () => {
  try { window.localStorage.setItem(COACHMARK_KEY, "true"); } catch (_) { /* ignore */ }
};

// Two-position toggle: Classic (v1) on the LEFT, the Updated UI (v2) on the RIGHT.
// `current` = the UI currently shown ("v1" | "v2"). Flipping switches apps:
// sets the sticky preference and does a clean full-page reload.
// `_navigate` is injectable for tests (defaults to window.location.assign).
export const UISwitch = ({ current = "v2", _navigate = (url) => window.location.assign(url) }) => {
  const isUpdated = current === "v2";
  const [showCoachmark, setShowCoachmark] = useState(() => !hasSeenCoachmark());

  const dismissCoachmark = () => {
    markCoachmarkSeen();
    setShowCoachmark(false);
  };
  const toggle = () => {
    dismissCoachmark();
    if (isUpdated) { chooseClassicUI(); _navigate("/"); }
    else { chooseModernUI(); _navigate("/v2"); }
  };
  return (
    <span className="ui-switch" style={{ position: "relative" }}>
      <span className="ui-switch__inner" title="Switch between the Classic and Updated UI">
        <span className={`ui-switch__label ui-switch__label--left${!isUpdated ? " is-active" : ""}`}>Classic</span>
        <button
          type="button"
          role="switch"
          aria-checked={isUpdated}
          aria-label="Toggle Classic / Updated UI"
          className={`ui-switch__track ${isUpdated ? "is-updated" : "is-classic"}`}
          data-testid="ui-switch"
          onClick={toggle}
        >
          <span className="ui-switch__knob" />
        </button>
        <span className={`ui-switch__label ui-switch__label--right${isUpdated ? " is-active" : ""}`}>Updated UI</span>
      </span>
      {showCoachmark && (
        <div className="ui-switch__coachmark" data-testid="ui-switch-coachmark" role="tooltip">
          <div className="ui-switch__coachmark-text">
            {isUpdated
              ? "You're on Plex's Updated UI — our newest interface. Switch back to Classic anytime."
              : "The Updated UI is our newest interface, with the same search underneath. Try it anytime."}
          </div>
          <button
            type="button"
            className="ui-switch__coachmark-dismiss"
            onClick={dismissCoachmark}
            data-testid="ui-switch-coachmark-dismiss"
          >
            Got it
          </button>
        </div>
      )}
    </span>
  );
};

export default UISwitch;
